/**
 * StaffName / StaffNameList — ảnh tròn bên TRÁI + tên nhân sự.
 * Tách tệp riêng khỏi StaffAvatar để các bài kiểm đang giả StaffAvatar
 * (`vi.mock("@/components/StaffAvatar", ...)`) vẫn dựng được tên.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { StaffAvatar } from "@/components/StaffAvatar";
import { StaffTitle } from "@/features/cosmetics/StaffTitle";

type NameProps = {
  staffId?: string | null | undefined;
  name: string;
  sizePx?: number | undefined;
  className?: string | undefined;
  /** Lớp riêng cho phần chữ (đậm, cắt dòng...). */
  nameClassName?: string | undefined;
  plain?: boolean | undefined;
  /** Chữ hiện thay cho tên (vd "Tôi"); ảnh vẫn tra theo staffId/name. */
  label?: ReactNode;
  /**
   * Danh hiệu sau tên (CEO 9a+b): bật ở chỗ người đó là chủ dòng/thẻ. Tên nằm
   * trong câu, ô chọn người, danh sách nhiều người thì để tắt — chèn vào là vỡ ô.
   */
  withTitle?: boolean | undefined;
};

/**
 * StaffName — ảnh tròn bên TRÁI + tên, dùng ở mọi chỗ hiện tên nhân sự
 * (CEO 19/10/2026: "mọi màn có tên nhân sự đều phải có ảnh thật bên trái").
 */
export function StaffName({
  staffId,
  name,
  sizePx = 20,
  className,
  nameClassName,
  plain,
  label,
  withTitle,
}: NameProps) {
  return (
    <span className={cn("inline-flex min-w-0 max-w-full items-center gap-1.5 align-middle", className)}>
      {/* Ảnh chỉ để nhìn — tên đã đứng ngay cạnh, máy đọc màn khỏi đọc chữ viết tắt/lặp tên. */}
      <span aria-hidden="true" className="inline-flex shrink-0">
        <StaffAvatar staffId={staffId} name={name} sizePx={sizePx} plain={plain} />
      </span>
      <span className={cn("min-w-0", nameClassName)}>{label ?? name}</span>
      {withTitle ? <StaffTitle staffId={staffId} name={name} className="shrink-0" /> : null}
    </span>
  );
}

type ListProps = {
  people: readonly ({ id?: string | null | undefined; name?: string | null | undefined } | string)[] | null | undefined;
  sizePx?: number | undefined;
  className?: string | undefined;
  nameClassName?: string | undefined;
  /** Hiện khi danh sách rỗng. */
  empty?: string | undefined;
  /** "row": nối thành hàng, tự xuống dòng; "column": mỗi người một dòng. */
  layout?: "row" | "column" | undefined;
};

/** Nhiều người — mỗi tên một ảnh, thay cho chuỗi "A, B, C". */
export function StaffNameList({ people, sizePx = 20, className, nameClassName, empty = "—", layout = "row" }: ListProps) {
  const list = (people ?? [])
    .map((p) => (typeof p === "string" ? { id: undefined, name: p } : { id: p.id ?? undefined, name: p.name ?? "" }))
    .map((p) => ({ ...p, name: p.name.trim() }))
    .filter((p) => p.name);
  if (list.length === 0) return <span className={className}>{empty}</span>;
  return (
    <span
      className={cn(
        "inline-flex min-w-0 max-w-full",
        layout === "column" ? "flex-col items-start gap-1" : "flex-wrap items-center gap-x-2.5 gap-y-1",
        className,
      )}
    >
      {list.map((p, i) => (
        <StaffName key={`${p.id ?? p.name}-${i}`} staffId={p.id} name={p.name} sizePx={sizePx} nameClassName={nameClassName} />
      ))}
    </span>
  );
}
